/**
 * Deep Link Validation Service
 *
 * Validates booking URLs by calling the server-side /api/validate-links endpoint,
 * which performs real HTTP HEAD requests and city mismatch detection.
 *
 * Running checks server-side avoids CORS restrictions that block browser fetch.
 * Falls back gracefully if the API is unavailable — never blocks the user.
 */

import { LogisticsData, LogisticsOption, DayItinerary, TimeSlot } from '../types';
import { buildGuaranteedUrl, isValidBookingUrl, isLikelyHallucinatedUrl, isLegacyAffiliateRedirect, wrapAffiliateLink, isKnownGoodDomain, setCachedUrl } from './linkService';

// ─── TYPES ──────────────────────────────────────────────────────────

export interface LinkCheckRequest {
  url: string;
  name: string;
  city: string;
  type: 'flight' | 'hotel' | 'restaurant' | 'activity' | 'attraction';
}

export interface LinkCheckResult {
  url: string;
  name?: string;
  city?: string;
  type?: string;
  isReachable: boolean;
  hasCityMismatch: boolean;
  statusCode?: number;
}

// ─── CORE VALIDATION CALL ───────────────────────────────────────────

/**
 * Calls the server-side validation endpoint with a batch of links.
 * Returns a map of url → result for fast lookup.
 * On any error (network, timeout, server down), returns an empty map
 * so callers treat all links as valid and never block the user.
 */
/**
 * Performs client-side domain-based validation when the server-side API is unavailable.
 * URLs on known booking/travel domains are trusted; unknown domains are flagged for replacement.
 */
const clientSideDomainValidation = (
  requests: LinkCheckRequest[]
): Map<string, LinkCheckResult> => {
  const resultMap = new Map<string, LinkCheckResult>();

  for (const req of requests) {
    try {
      const hostname = new URL(req.url).hostname.replace(/^www\./, '');
      const domainIsTrusted = isKnownGoodDomain(hostname);

      resultMap.set(req.url, {
        url: req.url,
        name: req.name,
        city: req.city,
        type: req.type,
        isReachable: domainIsTrusted,
        hasCityMismatch: false,
        statusCode: domainIsTrusted ? 200 : undefined,
      });

      if (!domainIsTrusted) {
        console.warn(
          `[LinkValidation] Client-side check: "${hostname}" is not a known travel domain — flagging "${req.name}" for replacement`
        );
      }
    } catch {
      // Malformed URL — flag as unreachable
      resultMap.set(req.url, {
        url: req.url,
        name: req.name,
        city: req.city,
        type: req.type,
        isReachable: false,
        hasCityMismatch: false,
      });
    }
  }

  return resultMap;
};

/**
 * Calls the server-side validation endpoint with a batch of links.
 * Returns a map of url → result for fast lookup.
 * When the API is unavailable (404, network error), falls back to client-side
 * domain-based validation instead of silently skipping all checks.
 */
export const deepValidateLinks = async (
  requests: LinkCheckRequest[]
): Promise<Map<string, LinkCheckResult>> => {
  if (!requests || requests.length === 0) return new Map();

  try {
    const response = await fetch('/api/validate-links', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ links: requests }),
      signal: AbortSignal.timeout(20_000), // 20s total — server does 6s/link in parallel
    });

    if (!response.ok) {
      console.warn(
        `[LinkValidation] API returned ${response.status} — falling back to client-side domain validation`
      );
      return clientSideDomainValidation(requests);
    }

    const data = await response.json();
    const resultMap = new Map<string, LinkCheckResult>();
    for (const r of (data.results as LinkCheckResult[])) {
      resultMap.set(r.url, r);
    }
    return resultMap;
  } catch (err: any) {
    const isTimeout = err?.name === 'TimeoutError' || err?.name === 'AbortError';
    console.warn(
      `[LinkValidation] Deep validation ${isTimeout ? 'timed out' : 'failed'} — falling back to client-side domain validation`,
      isTimeout ? '' : err?.message
    );
    return clientSideDomainValidation(requests);
  }
};

// ─── LOGISTICS DEEP VALIDATION ──────────────────────────────────────

/**
 * Deep-validates all booking URLs in a LogisticsData object.
 * Replaces any URL that fails HTTP reachability or city mismatch with a
 * guaranteed search-based fallback. Always resolves — never rejects.
 */
export const deepValidateLogisticsLinks = async (
  logistics: LogisticsData,
  destination: string
): Promise<LogisticsData> => {
  if (!logistics || !destination) return logistics;

  // Collect all logistics options and their link requests
  type Tagged = { option: LogisticsOption; type: 'flight' | 'hotel'; isAlt: boolean; altIndex?: number };
  const tagged: Tagged[] = [];

  if (logistics.flight) tagged.push({ option: logistics.flight, type: 'flight', isAlt: false });
  if (logistics.hotel) tagged.push({ option: logistics.hotel, type: 'hotel', isAlt: false });
  logistics.flightAlternatives?.forEach((o, i) => tagged.push({ option: o, type: 'flight', isAlt: true, altIndex: i }));
  logistics.hotelAlternatives?.forEach((o, i) => tagged.push({ option: o, type: 'hotel', isAlt: true, altIndex: i }));

  // Pre-filter: immediately replace hallucinated URLs before deep validation
  let preReplaced = 0;
  const preFixOption = (option: LogisticsOption, type: 'flight' | 'hotel'): LogisticsOption => {
    if (!option.bookingUrl || (!isLikelyHallucinatedUrl(option.bookingUrl) && !isLegacyAffiliateRedirect(option.bookingUrl))) return option;
    preReplaced++;
    const fallback = buildGuaranteedUrl(option.name || 'Travel', type, destination);
    const locationHint = [option.name, destination].filter(Boolean).join(', ');
    const finalUrl = option.affiliateType
      ? (() => { const w = wrapAffiliateLink(fallback, option.affiliateType!, locationHint); return w !== fallback ? w : fallback; })()
      : fallback;
    console.warn(
      `[LinkValidation] Pre-deep: replaced hallucinated ${type} link for "${option.name}":`,
      `"${option.bookingUrl.substring(0, 70)}" → "${finalUrl.substring(0, 70)}"`
    );
    return {
      ...option,
      bookingUrl: finalUrl,
      linkValidation: {
        ...(option.linkValidation || {}),
        validatedUrl: finalUrl,
        isOriginal: false,
        confidence: 1.0,
        domain: 'google.com',
        originalUrl: option.bookingUrl,
      },
    };
  };

  // Apply hallucination pre-filter to all options
  const preFixedLogistics: LogisticsData = {
    ...logistics,
    flight: logistics.flight ? preFixOption(logistics.flight, 'flight') : logistics.flight,
    hotel: logistics.hotel ? preFixOption(logistics.hotel, 'hotel') : logistics.hotel,
    flightAlternatives: logistics.flightAlternatives?.map(o => preFixOption(o, 'flight')),
    hotelAlternatives: logistics.hotelAlternatives?.map(o => preFixOption(o, 'hotel')),
  };

  if (preReplaced > 0) {
    console.log(`[LinkValidation] Pre-deep filter: replaced ${preReplaced} hallucinated URLs`);
  }

  // Collect remaining valid URLs for deep HTTP validation
  const preFixedTagged: Tagged[] = [];
  if (preFixedLogistics.flight) preFixedTagged.push({ option: preFixedLogistics.flight, type: 'flight', isAlt: false });
  if (preFixedLogistics.hotel) preFixedTagged.push({ option: preFixedLogistics.hotel, type: 'hotel', isAlt: false });
  preFixedLogistics.flightAlternatives?.forEach((o, i) => preFixedTagged.push({ option: o, type: 'flight', isAlt: true, altIndex: i }));
  preFixedLogistics.hotelAlternatives?.forEach((o, i) => preFixedTagged.push({ option: o, type: 'hotel', isAlt: true, altIndex: i }));

  const requests: LinkCheckRequest[] = preFixedTagged
    .filter(t => isValidBookingUrl(t.option.bookingUrl))
    .map(t => ({
      url: t.option.bookingUrl!,
      name: t.option.name || '',
      city: destination,
      type: t.type,
    }));

  if (requests.length === 0) return preFixedLogistics;

  const results = await deepValidateLinks(requests);
  if (results.size === 0) return preFixedLogistics; // API unavailable — keep pre-fixed links

  let replacedCount = 0;

  const fixOption = (option: LogisticsOption, type: 'flight' | 'hotel'): LogisticsOption => {
    if (!option.bookingUrl) return option;
    const result = results.get(option.bookingUrl);
    if (!result) return option; // URL wasn't checked (format-invalid) — keep as-is

    const failed = !result.isReachable || result.hasCityMismatch;
    if (!failed) {
      // Cache the validated URL for future use
      setCachedUrl(option.name || '', type, destination, option.bookingUrl!);
      return option;
    }

    replacedCount++;
    const reason = result.hasCityMismatch ? 'city mismatch' : `HTTP ${result.statusCode ?? 'unreachable'}`;
    const fallback = buildGuaranteedUrl(option.name || 'Travel', type, destination);
    const locationHint = [option.name, destination].filter(Boolean).join(', ');
    const finalUrl = option.affiliateType
      ? (() => { const w = wrapAffiliateLink(fallback, option.affiliateType!, locationHint); return w !== fallback ? w : fallback; })()
      : fallback;
    console.warn(
      `[LinkValidation] Replaced ${type} link for "${option.name}" (${reason}):`,
      `"${option.bookingUrl.substring(0, 70)}"`,
      `→ "${finalUrl.substring(0, 70)}"`
    );
    return {
      ...option,
      bookingUrl: finalUrl,
      linkValidation: {
        ...(option.linkValidation || {}),
        validatedUrl: finalUrl,
        isOriginal: false,
        confidence: 1.0,
        domain: 'google.com',
        originalUrl: option.bookingUrl,
      },
    };
  };

  const result: LogisticsData = {
    ...preFixedLogistics,
    flight: preFixedLogistics.flight ? fixOption(preFixedLogistics.flight, 'flight') : preFixedLogistics.flight,
    hotel: preFixedLogistics.hotel ? fixOption(preFixedLogistics.hotel, 'hotel') : preFixedLogistics.hotel,
    flightAlternatives: preFixedLogistics.flightAlternatives?.map(o => fixOption(o, 'flight')),
    hotelAlternatives: preFixedLogistics.hotelAlternatives?.map(o => fixOption(o, 'hotel')),
  };

  const checked = requests.length;
  const passed = checked - replacedCount;
  console.log(
    `[LinkValidation] Logistics deep check: ${passed}/${checked} links passed,`,
    `${replacedCount} replaced. Accuracy: ${checked > 0 ? ((passed / checked) * 100).toFixed(0) : 100}%`
  );

  return result;
};

// ─── ACTIVITY DEEP VALIDATION ────────────────────────────────────────

/**
 * Deep-validates all booking URLs in a single DayItinerary.
 * Replaces failing URLs with guaranteed search fallbacks.
 * Designed to be called per-day as days stream in, non-blocking.
 */
export const deepValidateDayLinks = async (
  day: DayItinerary,
  destination: string
): Promise<DayItinerary> => {
  if (!day || day.isPlaceholder || !destination) return day;

  // Pre-filter: replace hallucinated/legacy-affiliate URLs before deep validation
  const preFixSlot = (slot: TimeSlot | undefined): TimeSlot | undefined => {
    if (!slot?.bookingUrl || (!isLikelyHallucinatedUrl(slot.bookingUrl) && !isLegacyAffiliateRedirect(slot.bookingUrl))) return slot;
    const fallback = buildGuaranteedUrl(slot.activity || 'Experience', 'activity', destination);
    console.warn(
      `[LinkValidation] Pre-deep: replaced hallucinated activity link for "${slot.activity}":`,
      `"${slot.bookingUrl.substring(0, 70)}" → "${fallback.substring(0, 70)}"`
    );
    return { ...slot, bookingUrl: fallback };
  };

  const preFixedDay: DayItinerary = {
    ...day,
    morning: preFixSlot(day.morning) ?? day.morning,
    afternoon: preFixSlot(day.afternoon) ?? day.afternoon,
    evening: preFixSlot(day.evening) ?? day.evening,
    contingency: day.contingency ? (preFixSlot(day.contingency) ?? day.contingency) : day.contingency,
  };

  const slots: Array<{ key: 'morning' | 'afternoon' | 'evening' | 'contingency'; slot: TimeSlot }> = [];
  if (preFixedDay.morning) slots.push({ key: 'morning', slot: preFixedDay.morning });
  if (preFixedDay.afternoon) slots.push({ key: 'afternoon', slot: preFixedDay.afternoon });
  if (preFixedDay.evening) slots.push({ key: 'evening', slot: preFixedDay.evening });
  if (preFixedDay.contingency) slots.push({ key: 'contingency', slot: preFixedDay.contingency });

  const requests: LinkCheckRequest[] = slots
    .filter(s => isValidBookingUrl(s.slot.bookingUrl))
    .map(s => ({
      url: s.slot.bookingUrl!,
      name: s.slot.activity || '',
      city: destination,
      type: 'activity' as const,
    }));

  if (requests.length === 0) return preFixedDay;

  const results = await deepValidateLinks(requests);
  if (results.size === 0) return preFixedDay;

  let replacedCount = 0;

  const fixSlot = (slot: TimeSlot): TimeSlot => {
    if (!slot.bookingUrl) return slot;
    const result = results.get(slot.bookingUrl);
    if (!result) return slot;

    const failed = !result.isReachable || result.hasCityMismatch;
    if (!failed) return slot;

    replacedCount++;
    const reason = result.hasCityMismatch ? 'city mismatch' : `HTTP ${result.statusCode ?? 'unreachable'}`;
    const fallback = buildGuaranteedUrl(slot.activity || 'Experience', 'activity', destination);
    console.warn(
      `[LinkValidation] Replaced activity link for "${slot.activity}" (${reason}):`,
      `"${slot.bookingUrl.substring(0, 70)}"`,
      `→ "${fallback.substring(0, 70)}"`
    );
    return { ...slot, bookingUrl: fallback };
  };

  return {
    ...preFixedDay,
    morning: preFixedDay.morning ? fixSlot(preFixedDay.morning) : preFixedDay.morning,
    afternoon: preFixedDay.afternoon ? fixSlot(preFixedDay.afternoon) : preFixedDay.afternoon,
    evening: preFixedDay.evening ? fixSlot(preFixedDay.evening) : preFixedDay.evening,
    contingency: preFixedDay.contingency ? fixSlot(preFixedDay.contingency) : preFixedDay.contingency,
  };
};

/**
 * Deep-validates all days in an itinerary in parallel (batched 5 days at a time).
 * Non-blocking — callers can fire-and-forget and update state when done.
 */
export const deepValidateItineraryLinks = async (
  itinerary: DayItinerary[],
  destination: string
): Promise<DayItinerary[]> => {
  if (!itinerary || itinerary.length === 0 || !destination) return itinerary;

  // Pre-filter: replace hallucinated URLs in all slots before deep validation
  const preFixedItinerary = itinerary.map(day => {
    if (day.isPlaceholder) return day;
    const preFixSlot = (slot: TimeSlot | undefined): TimeSlot | undefined => {
      if (!slot?.bookingUrl || (!isLikelyHallucinatedUrl(slot.bookingUrl) && !isLegacyAffiliateRedirect(slot.bookingUrl))) return slot;
      const fallback = buildGuaranteedUrl(slot.activity || 'Experience', 'activity', destination);
      console.warn(
        `[LinkValidation] Pre-deep itinerary: replaced hallucinated link for "${slot.activity}":`,
        `"${slot.bookingUrl.substring(0, 70)}" → "${fallback.substring(0, 70)}"`
      );
      return { ...slot, bookingUrl: fallback };
    };
    return {
      ...day,
      morning: preFixSlot(day.morning) ?? day.morning,
      afternoon: preFixSlot(day.afternoon) ?? day.afternoon,
      evening: preFixSlot(day.evening) ?? day.evening,
      contingency: day.contingency ? (preFixSlot(day.contingency) ?? day.contingency) : day.contingency,
    };
  });

  // Batch all slots into a single API call for efficiency
  type SlotRef = { dayIndex: number; key: 'morning' | 'afternoon' | 'evening' | 'contingency' };
  const requests: (LinkCheckRequest & SlotRef)[] = [];

  preFixedItinerary.forEach((day, dayIndex) => {
    if (day.isPlaceholder) return;
    const keys = ['morning', 'afternoon', 'evening', 'contingency'] as const;
    for (const key of keys) {
      const slot = day[key];
      if (slot && isValidBookingUrl(slot.bookingUrl)) {
        requests.push({
          url: slot.bookingUrl!,
          name: slot.activity || '',
          city: destination,
          type: 'activity',
          dayIndex,
          key,
        });
      }
    }
  });

  if (requests.length === 0) return preFixedItinerary;

  const results = await deepValidateLinks(requests);
  if (results.size === 0) return preFixedItinerary;

  let replacedCount = 0;
  const days = preFixedItinerary.map((day, dayIndex) => {
    if (day.isPlaceholder) return day;

    const fixSlot = (slot: TimeSlot | undefined): TimeSlot | undefined => {
      if (!slot?.bookingUrl) return slot;
      const result = results.get(slot.bookingUrl);
      if (!result || (result.isReachable && !result.hasCityMismatch)) return slot;

      replacedCount++;
      const reason = result.hasCityMismatch ? 'city mismatch' : `HTTP ${result.statusCode ?? 'unreachable'}`;
      const fallback = buildGuaranteedUrl(slot.activity || 'Experience', 'activity', destination);
      console.warn(`[LinkValidation] Day ${dayIndex + 1} — replaced "${slot.activity}" (${reason})`);
      return { ...slot, bookingUrl: fallback };
    };

    return {
      ...day,
      morning: fixSlot(day.morning) ?? day.morning,
      afternoon: fixSlot(day.afternoon) ?? day.afternoon,
      evening: fixSlot(day.evening) ?? day.evening,
      contingency: day.contingency ? (fixSlot(day.contingency) ?? day.contingency) : day.contingency,
    };
  });

  const checked = requests.length;
  const passed = checked - replacedCount;
  console.log(
    `[LinkValidation] Itinerary deep check: ${passed}/${checked} activity links passed,`,
    `${replacedCount} replaced. Accuracy: ${checked > 0 ? ((passed / checked) * 100).toFixed(0) : 100}%`
  );

  return days;
};
